import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import {
  U21Button,
  U21MenuList,
  U21MenuListOptionProps,
  U21Spacer,
  U21Tooltip,
} from 'app/shared/u21-ui/components';
import { IconAlertTriangle, IconCheck, IconTrash } from '@u21/tabler-icons';

import { DeleteSavedFilterModal } from 'app/modules/filters/components/DeleteSavedFilterModal';
import { FilterContext, SavedFilter } from 'app/modules/filters/models';
import { selectAgent } from 'app/modules/session/selectors';

interface Props {
  appliedSavedFilterIds: Set<number>;
  filterContext: FilterContext;
  onApply: (savedFilter: SavedFilter) => void;
  onDeleted: (savedFilter: SavedFilter) => void;
  savedFilters: SavedFilter[];
  staleSavedFilterIds: Set<number>;
}

export const SavedFilterPanel = ({
  appliedSavedFilterIds,
  filterContext,
  onApply,
  onDeleted,
  savedFilters,
  staleSavedFilterIds,
}: Props) => {
  const agent = useSelector(selectAgent);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SavedFilter | undefined>(
    undefined,
  );

  const options = useMemo<U21MenuListOptionProps[]>(() => {
    return savedFilters.map((i) => {
      const isOwner = i.created_by_id === agent.id;
      const isApplied = appliedSavedFilterIds.has(i.id);
      const isStale = staleSavedFilterIds.has(i.id);

      const rightIcon = (
        <U21Spacer horizontal>
          {isApplied && <AppliedCheck />}
          {!isApplied && isStale && (
            <U21Tooltip tooltip="Some filters in this saved filter may be unavailable.">
              <StaleWarning />
            </U21Tooltip>
          )}
          {isOwner && (
            <U21Button
              aria-label={`Delete ${i.title}`}
              color="error"
              icon={<IconTrash />}
              onClick={(e) => {
                e.stopPropagation();
                setPendingDelete(i);
                setDeleteOpen(true);
              }}
              size="small"
              variant="ghost"
            />
          )}
        </U21Spacer>
      );

      return {
        description: i.description ?? undefined,
        onClick: () => onApply(i),
        rightIcon,
        text: i.title,
        typographyProps: { ellipsis: true },
      };
    });
  }, [
    agent.id,
    appliedSavedFilterIds,
    staleSavedFilterIds,
    savedFilters,
    onApply,
  ]);

  return (
    <>
      <U21MenuList options={options} placeholder="Search saved filters" />
      {pendingDelete && (
        <DeleteSavedFilterModal
          filterContext={filterContext}
          onClose={() => setDeleteOpen(false)}
          onDeleted={onDeleted}
          open={deleteOpen}
          savedFilter={pendingDelete}
        />
      )}
    </>
  );
};

const AppliedCheck = styled(IconCheck)`
  color: ${({ theme }) => theme.palette.primary.main};
`;

const StaleWarning = styled(IconAlertTriangle)`
  color: ${({ theme }) => theme.palette.warning.main};
`;
